import { useState, useRef, useEffect, useCallback } from "react";
import {
  Clock, MessageSquare, RotateCcw, CheckCircle2, XCircle, MinusCircle,
  AlertTriangle, X, Trash2, Lock,
} from "lucide-react";
import "./QuizViewer.css";

const BASE = process.env.REACT_APP_API_URL;

function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function formatDeadline(iso) {
  return new Date(iso).toLocaleDateString("es-PE", {
    day: "numeric", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Banner de deadline ────────────────────────────────────────────
function DeadlineBanner({ deadline, isClosed }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!deadline || isClosed) return;
    const update = () => {
      const diff = new Date(deadline) - new Date();
      if (diff <= 0) { setTimeLeft(""); return; }
      const d = Math.floor(diff / 864e5);
      const h = Math.floor((diff % 864e5) / 36e5);
      const m = Math.floor((diff % 36e5) / 60000);
      if (d > 0)      setTimeLeft(`${d}d ${h}h restantes`);
      else if (h > 0) setTimeLeft(`${h}h ${m}min restantes`);
      else            setTimeLeft(`${m}min restantes`);
    };
    update();
    const t = setInterval(update, 30_000);
    return () => clearInterval(t);
  }, [deadline, isClosed]);

  if (!deadline) return null;

  if (isClosed) {
    return (
      <div style={{
        display:"flex", alignItems:"flex-start", gap:".8rem",
        background:"#fde8e8", border:"0.5px solid #e8b0b0",
        borderLeft:"4px solid #c0392b",
        borderRadius:8, padding:"1rem 1.2rem", marginBottom:"1.2rem",
      }}>
        <Lock size={18} color="#c0392b" style={{ flexShrink:0, marginTop:2 }} />
        <div>
          <p style={{
            fontFamily:"var(--font-display)", fontWeight:800,
            fontSize:"0.88rem", color:"#c0392b", marginBottom:".25rem",
          }}>
            Plazo de entrega cerrado
          </p>
          <p style={{ fontSize:"0.78rem", color:"#a03020", lineHeight:1.6 }}>
            Este cuestionario cerró el <strong>{formatDeadline(deadline)}</strong>.
            Puedes revisar tus resultados y debatir con el asistente, pero ya no es posible enviar respuestas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display:"flex", alignItems:"center", gap:".8rem",
      background:"#fef3e7", border:"0.5px solid #f0c080",
      borderLeft:"4px solid #e67e22",
      borderRadius:8, padding:".8rem 1.2rem", marginBottom:"1.2rem",
    }}>
      <Clock size={16} color="#e67e22" style={{ flexShrink:0 }} />
      <div style={{ flex:1 }}>
        <p style={{
          fontFamily:"var(--font-display)", fontWeight:700,
          fontSize:"0.82rem", color:"#b05a00", marginBottom:".1rem",
        }}>
          Entrega hasta el {formatDeadline(deadline)}
        </p>
        {timeLeft && (
          <p style={{ fontSize:"0.72rem", color:"#c07020" }}>⏳ {timeLeft}</p>
        )}
      </div>
    </div>
  );
}

// ── Modal de confirmación ─────────────────────────────────────────
function ConfirmModal({ title, description, onConfirm, onCancel, loading }) {
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:700,
      display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem",
    }}>
      <div style={{
        background:"var(--white)", borderRadius:"var(--card-radius)", padding:"2rem 1.8rem",
        maxWidth:400, width:"100%", boxShadow:"0 8px 32px rgba(0,0,0,0.18)",
        border:"0.5px solid var(--border)",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.2rem" }}>
          <div style={{
            width:48, height:48, borderRadius:"50%", background:"#fde8e8",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}>
            <AlertTriangle size={22} color="#c0392b" strokeWidth={2} />
          </div>
          <button onClick={onCancel} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-muted)", padding:4 }}>
            <X size={18} />
          </button>
        </div>
        <h3 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1rem", color:"var(--charcoal)", marginBottom:".5rem" }}>
          {title}
        </h3>
        <p style={{ fontFamily:"var(--font-body)", fontSize:"0.82rem", color:"var(--text-muted)", marginBottom:"1.4rem" }}>
          {description}
        </p>
        <div style={{ display:"flex", gap:".7rem", justifyContent:"flex-end" }}>
          <button className="btn-outline" onClick={onCancel} disabled={loading} style={{ minWidth:90 }}>
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              display:"flex", alignItems:"center", gap:".4rem",
              background:"#c0392b", color:"#fff", border:"none",
              borderRadius:6, padding:".55rem 1.1rem",
              fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.82rem",
              cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} strokeWidth={2.5} />
            {loading ? "Eliminando..." : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuizViewer({ quiz, deadline = null, onSubmit, onStartDebate, isAdmin = false }) {
  const alreadyDone = !!quiz.previousResults;

  // ── Estado de deadline ──────────────────────────────────────────
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const deadlineDate = deadline ? new Date(deadline) : null;
  const isClosed     = deadlineDate ? now > deadlineDate : false;

  const [started,   setStarted]   = useState(alreadyDone);
  const [answers,   setAnswers]   = useState(quiz.previousAnswers ?? {});
  const [submitted, setSubmitted] = useState(alreadyDone);
  const [results,   setResults]   = useState(quiz.previousResults ?? null);
  const [loading,   setLoading]   = useState(false);
  const [errorMsg,  setErrorMsg]  = useState("");

  // ── Temporizador ────────────────────────────────────────────────
  const timeLimit  = quiz.time_limit_seconds ?? null;
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [expired,  setExpired]  = useState(false);
  const timerRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => {
    if (!started || submitted || timeLimit === null) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { stopTimer(); setExpired(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return stopTimer;
  }, [started, submitted, timeLimit, stopTimer]);

  useEffect(() => {
    if (expired && !submitted) handleSubmit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired]);

  // ── Scroll a resultados ─────────────────────────────────────────
  const resultsRef  = useRef(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (submitted && results && resultsRef.current && !scrolledRef.current && !alreadyDone) {
      scrolledRef.current = true;
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior:"smooth", block:"start" });
      }, 80);
    }
  }, [submitted, results, alreadyDone]);

  // ── Admin reset ─────────────────────────────────────────────────
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetMsg,  setResetMsg]  = useState("");

  const handleAdminReset = async () => {
    setShowResetConfirm(false);
    setResetting(true);
    setResetMsg("");
    try {
      const res = await fetch(`${BASE}/admin/quizzes/${quiz.class_id}/responses/me`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Error al resetear");
      }
      setResetMsg("Respuestas eliminadas. Recargando...");
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setResetMsg("Error: " + err.message);
    } finally {
      setResetting(false);
    }
  };

  // ── Lógica principal ────────────────────────────────────────────
  const select = (questionId, optionIdx) => {
    if (submitted || isClosed) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionIdx }));
  };

  const handleSubmit = async () => {
    stopTimer();
    setLoading(true);
    setErrorMsg("");
    const payload = quiz.questions.map((q) => ({
      question_id: q.id,
      answer: answers[q.id] ?? null,
    }));
    try {
      const res = await onSubmit(payload);
      setResults(res);
      setSubmitted(true);
    } catch (err) {
      setErrorMsg(err.message || "No se pudo enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const buildDebateContext = () =>
    quiz.questions.map((q, i) => {
      const opts    = Array.isArray(q.options) ? q.options : JSON.parse(q.options);
      const userIdx = answers[q.id];
      const corrIdx = Number(q.correct);
      return {
        index:      i + 1,
        question:   q.question,
        userAnswer: userIdx != null ? opts[userIdx] : "Sin responder",
        correct:    opts[corrIdx],
        wasCorrect: userIdx === corrIdx,
      };
    });

  const handleStartDebate = () => {
    if (onStartDebate) onStartDebate(buildDebateContext(), results);
  };

  const pctVal = (n) => results ? Math.round((n / results.total) * 100) : 0;

  const timerColor = () => {
    if (timeLeft === null) return "var(--teal)";
    const ratio = timeLeft / timeLimit;
    if (ratio > 0.4)  return "#27ae60";
    if (ratio > 0.15) return "#e07b25";
    return "#c0392b";
  };

  // ── Pantalla de inicio ───────────────────────────────────────────
  if (!started) {
    return (
      <div className="qv-start-wrap">
        <div className="qv-start-icon">📝</div>
        <h3 className="qv-start-title">{quiz.title}</h3>
        <p className="qv-start-desc">
          {quiz.questions.length} preguntas · Selecciona la mejor respuesta para cada una
        </p>
        {timeLimit && (
          <p className="qv-start-timer-note" style={{ display:"flex", alignItems:"center", gap:".4rem", justifyContent:"center" }}>
            <Clock size={14} /> Tiempo límite: <strong>{formatTime(timeLimit)}</strong>
          </p>
        )}

        {/* Deadline en pantalla de inicio */}
        {deadline && (
          <div style={{
            margin: ".8rem auto 0",
            maxWidth: 360,
          }}>
            <DeadlineBanner deadline={deadline} isClosed={isClosed} />
          </div>
        )}

        <button
          className="btn-primary qv-start-btn"
          onClick={() => setStarted(true)}
          disabled={isClosed}
          style={isClosed ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          {isClosed ? "Plazo cerrado" : "Iniciar cuestionario"}
        </button>

        {isClosed && (
          <p style={{ fontSize:"0.75rem", color:"var(--text-muted)", marginTop:".5rem" }}>
            Ya no es posible responder este cuestionario.
          </p>
        )}
      </div>
    );
  }

  // ── Cuestionario activo ──────────────────────────────────────────
  return (
    <div className="qv-wrap">

      {showResetConfirm && (
        <ConfirmModal
          title="¿Resetear tus respuestas?"
          description="Se eliminarán todas tus respuestas en este quiz para que puedas volver a intentarlo. Esta acción no se puede deshacer."
          loading={resetting}
          onConfirm={handleAdminReset}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}

      {/* Header */}
      <div className="qv-header">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:".5rem" }}>
          <div>
            <h2 className="qv-title">{quiz.title}</h2>
            <p className="qv-count">{quiz.questions.length} preguntas</p>
            {alreadyDone && (
              <p style={{ fontSize:"0.8rem", color:"white", marginTop:".3rem" }}>
                Ya respondiste este cuestionario — tus respuestas están bloqueadas.
              </p>
            )}
          </div>

          {timeLimit !== null && !submitted && (
            <div className="qv-timer" style={{ color:timerColor(), borderColor:timerColor() }}>
              <Clock size={14} className="qv-timer-icon" />
              <span className="qv-timer-value" style={{ color:timerColor() }}>
                {expired ? "00:00" : formatTime(timeLeft)}
              </span>
            </div>
          )}
        </div>
      </div>

      {expired && !submitted && (
        <div className="qv-expired-banner">
          <Clock size={15} /> Tiempo agotado — enviando respuestas automáticamente…
        </div>
      )}

      {/* Banner de deadline (dentro del quiz activo) */}
      {!submitted && (
        <div style={{ padding:"0 0 .2rem" }}>
          <DeadlineBanner deadline={deadline} isClosed={isClosed} />
        </div>
      )}

      {/* Resultados */}
      {submitted && results && (
        <div className="qv-results" ref={resultsRef}>
          {/* Banner encima de resultados si está cerrado */}
          <DeadlineBanner deadline={deadline} isClosed={isClosed} />

          <div className="qv-result-score">
            <span className="qv-score-num">{results.correct}</span>
            <span className="qv-score-of">/ {results.total}</span>
          </div>
          <p className="qv-score-label">respuestas correctas</p>
          <div className="qv-result-bars">
            {[
              { label:"Correctas",    val:results.correct, cls:"correct", Icon:CheckCircle2 },
              { label:"Incorrectas",  val:results.wrong,   cls:"wrong",   Icon:XCircle      },
              { label:"Sin responder",val:results.empty,   cls:"empty",   Icon:MinusCircle  },
            ].map((s) => (
              <div className="qv-bar-item" key={s.label}>
                <span className={`qv-bar-label ${s.cls}`} style={{ display:"flex", alignItems:"center", gap:".3rem" }}>
                  <s.Icon size={13} strokeWidth={2.5} /> {s.label}
                </span>
                <div className="qv-bar-track">
                  <div className={`qv-bar-fill ${s.cls}`} style={{ width:`${pctVal(s.val)}%` }} />
                </div>
                <span className="qv-bar-val">{s.val}</span>
              </div>
            ))}
          </div>

          {onStartDebate && (
            <button className="qv-debate-btn" onClick={handleStartDebate}
              style={{ display:"flex", alignItems:"center", gap:".5rem", justifyContent:"center" }}>
              <MessageSquare size={16} strokeWidth={2} />
              Debatir mis respuestas con el asistente
            </button>
          )}

          {isAdmin && (
            <div style={{ marginTop:"1rem", borderTop:"0.5px solid #eee", paddingTop:"1rem" }}>
              <p style={{ fontSize:"0.72rem", color:"#888", marginBottom:".5rem", textAlign:"center" }}>
                Panel de administrador — modo prueba
              </p>
              {resetMsg && (
                <p style={{ fontSize:"0.78rem", color:resetMsg.startsWith("Error") ? "#c0392b" : "#27ae60", marginBottom:".5rem", textAlign:"center" }}>
                  {resetMsg}
                </p>
              )}
              <button
                className="qv-reset-btn"
                onClick={() => setShowResetConfirm(true)}
                disabled={resetting}
                style={{ display:"flex", alignItems:"center", gap:".5rem", justifyContent:"center", margin:"0 auto" }}
              >
                <RotateCcw size={14} strokeWidth={2.5} />
                {resetting ? "Eliminando..." : "Resetear mis respuestas"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Preguntas */}
      <div className="qv-questions">
        {quiz.questions.map((q, qi) => {
          const userAnswer = answers[q.id];
          const opts = Array.isArray(q.options) ? q.options : JSON.parse(q.options);

          return (
            <div className={`qv-card ${submitted || isClosed ? "done" : ""}`} key={q.id}>
              <p className="qv-q-num">Pregunta {qi + 1}</p>
              <p className="qv-q-text">{q.question}</p>
              <div className="qv-options">
                {opts.map((opt, oi) => {
                  const correctIdx = Number(q.correct);
                  let state = "";
                  if (submitted) {
                    if (oi === correctIdx)                            state = "correct";
                    else if (oi === userAnswer && oi !== correctIdx)  state = "wrong";
                  } else if (isClosed) {
                    // cerrado pero no respondido: solo highlight de selección previa si existe
                    if (userAnswer === oi) state = "selected";
                  } else {
                    if (userAnswer === oi) state = "selected";
                  }

                  return (
                    <div
                      key={oi}
                      className={`qv-option ${state}`}
                      onClick={() => select(q.id, oi)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && select(q.id, oi)}
                      style={isClosed && !submitted ? { cursor:"default", opacity: 0.75 } : {}}
                    >
                      <span className="qv-opt-letter">{["A","B","C","D"][oi]}</span>
                      <span className="qv-opt-text">{opt}</span>
                      {submitted && oi === correctIdx && (
                        <CheckCircle2 size={15} strokeWidth={2.5} className="qv-opt-icon" />
                      )}
                      {submitted && oi === userAnswer && oi !== correctIdx && (
                        <XCircle size={15} strokeWidth={2.5} className="qv-opt-icon wrong" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Botón enviar — oculto si cerrado o ya enviado */}
      {!submitted && !isClosed && (
        <div className="qv-footer">
          {errorMsg && (
            <p style={{ color:"#c0392b", fontSize:"0.8rem", marginBottom:".5rem" }}>{errorMsg}</p>
          )}
          <p className="qv-answered">
            {Object.keys(answers).length} de {quiz.questions.length} respondidas
          </p>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading || expired}>
            {loading ? "Enviando..." : "Enviar respuestas"}
          </button>
        </div>
      )}

      {/* Pie cuando está cerrado y no hay resultados propios */}
      {isClosed && !submitted && (
        <div className="qv-footer" style={{ justifyContent:"center" }}>
          <div style={{
            display:"flex", alignItems:"center", gap:".5rem",
            color:"var(--text-muted)", fontSize:"0.8rem",
            fontFamily:"var(--font-display)", fontWeight:600,
          }}>
            <Lock size={14} /> El plazo de entrega ha finalizado
          </div>
          {onStartDebate && (
            <button
              className="qv-debate-btn"
              onClick={() => onStartDebate(buildDebateContext(), { correct:0, wrong:0, empty: quiz.questions.length, total: quiz.questions.length })}
              style={{ display:"flex", alignItems:"center", gap:".5rem", justifyContent:"center", marginTop:".8rem" }}
            >
              <MessageSquare size={16} strokeWidth={2} />
              Debatir con el asistente
            </button>
          )}
        </div>
      )}
    </div>
  );
}